import { Injectable, Injector } from "@angular/core";
import { Http, RequestOptionsArgs, Headers } from "@angular/http";
import {
    ITCIApplication,
    ITCIApplicationMonitoring,
    ITCIApplicationMonitoringResponse,
    ITCIApplicationsMonitoringRequest,
    ITCIMonitoringInstance,
    ITCIMonitoringInstances, QUERY_PARAMS, SERVICE_URLS,
    BASE_CONSTANTS,
    TCI_APP_MONITORING_REQUEST_TYPE,
    TCITenantAppType,
    ITCIApplicationInstanceInfo,
    ITCIApplicationPodInfo
} from "@tci/common";
import { TCITenantMenuServices } from "../../../ng-tenant/tci-tenant.menu";
import { BaseTCIApplicationMonitoringProvider } from "../../../ng-tenant/tci-tenant-services";
import { NgUserService } from "../../../ng-shared/ng-services/ng-user.service";
import { NgSecurityService } from "../../../ng-shared/ng-services/ng-security.service";
import { TciTenantModelService } from "../../../ng-tenant/tci-tenant.model";

import { Observable, Subject } from "rxjs";
import * as lodash from "lodash";

@Injectable()
export class BaseAppDetailMonitoringService extends BaseTCIApplicationMonitoringProvider {
    private application: ITCIApplication;
    private selectedInstance: ITCIMonitoringInstance;
    private monitorSubjectCollection: { [key: string]: Subject<ITCIApplicationMonitoringResponse> } = {};

    private instReqRespCache: ITCIApplicationMonitoringResponse; // Store the instances request response

    constructor(
        _injector: Injector,
        _http: Http,
        private security: NgSecurityService,
        private tenantModelSvc: TciTenantModelService,
        private userService: NgUserService
    ) {
        super(_injector, _http);
    }

    get http(): Http {
        return this._http;
    }

    get injector(): Injector {
        return this._injector;
    }

    private getGenericResponse(appId: string, request_type: TCI_APP_MONITORING_REQUEST_TYPE): ITCIApplicationMonitoringResponse {
        const result: ITCIApplicationMonitoring = { selectedInstance: this.selectedInstance };
        return {
            request_type, appId, result,
            success: true,
            msg: null, // TODO: Not needed
        };
    }

    startAppMonitoringSubscription(input: ITCIApplicationsMonitoringRequest): Observable<boolean> {
        this.application = input.application;
        return Observable.of(true);
    }

    stopAppMonitoringSubscription(input: ITCIApplicationsMonitoringRequest): Observable<boolean> {
        if (input.application.type.equals(TCITenantAppType.node) && this.application.id === input.appId) {
            delete this.application;

            // TODO: Stop all communication to back-end wss

            return Observable.of(true);
        } else if (this.application.id !== input.appId) {
            // console.error("Monitor try to unregister a different app than the current one:", this.application.id, "vs", input.appId);
            return Observable.of(false);
        } else {
            // console.error("Monitor try to unregister a non-nodejs app");
            return Observable.of(false);
        }
    }

    // This method is executed BEFORE "getAppInstances" and stores the instances result for that method
    canShowInstanceSelector(input: ITCIApplicationsMonitoringRequest): Observable<boolean> {
        const options = {
            headers: new Headers({ "Content-Type": "application/json" }),
            body: input
        } as RequestOptionsArgs;
        const appType = input.applicationType ? input.applicationType : input.tenantType.toString();
        const response = {
            msg: "",
            success: true,
            request_type: TCI_APP_MONITORING_REQUEST_TYPE.APP_INSTANCE_LIST,
            result: {
                instances: {} as ITCIMonitoringInstances
            } as ITCIApplicationMonitoring
        } as ITCIApplicationMonitoringResponse;
        let url = `${SERVICE_URLS.METRICS_DIRECT}/${input.appId}?${QUERY_PARAMS.APP_TYPE}=${appType}&${QUERY_PARAMS.ON_PREMISE}=${input.application.onPremise}`;
        if (this.injector.get(TCITenantMenuServices).isMigrationReadytag(input.application.metaTags)) {
            url += `&${QUERY_PARAMS.LEGACY_STATE}=${BASE_CONSTANTS.MIGRATION_READY}`;
        }
        return this.http.get(url, options)
            .map(_res => _res.json())
            .catch(err => {
                let msg = "";
                try {
                    msg = err.json().errorMsg;
                } catch (e) {
                    msg = "Error fetching instances";
                }
                response.success = false;
                response.msg = msg;
                response.appId = input.appId;
                return Observable.throw(response);
            })
            .map((instancesData: ITCIApplicationInstanceInfo) => {
                const instances: ITCIMonitoringInstances = {};
                const createResponse = (inst: ITCIMonitoringInstances) => {
                    response.result.instances = inst;
                    response.appId = input.appId;
                    response.success = true;
                };
                if (instancesData &&
                    instancesData.instances &&
                    Array.isArray(instancesData.instances) &&
                    instancesData.instances.length > 0) {
                    /**
                     * CIC1 response: instancesData.instances: string[]
                     */
                    instancesData.instances.forEach((key: string) => instances[key] = { key, label: key.substring(0, 12) });
                    createResponse(instances);
                } else if (instancesData &&
                    instancesData.pods &&
                    Array.isArray(instancesData.pods) &&
                    instancesData.pods.length > 0) {
                    /**
                     * CIC2 response: instancesData.pods: ITCIApplicationPodInfo[]
                     * https://jira.tibco.com/browse/IPAS-18285
                     */
                    instancesData.pods.forEach((key: ITCIApplicationPodInfo) => {
                        instances[key.podID] = {
                            key: key.podID,
                            label: key.podID.substring(0, 12),
                            ip: key.podIP
                        };
                    });
                    createResponse(instances);
                } else {
                }

                this.instReqRespCache = response;
                return response.success && lodash.size(instances) > 0;
            });
    }

    getAppInstances(input: ITCIApplicationsMonitoringRequest): Observable<ITCIApplicationMonitoringResponse> {
        return Observable.of(this.instReqRespCache);
    }

    canShowMetricsCollection(input: ITCIApplicationsMonitoringRequest): Observable<boolean> {
        return undefined;
    }

    /**
     * Return a subject per request type
     * If it does not exists it creates it
     * @param type
     */
    getAppMonitoringSubject(type: TCI_APP_MONITORING_REQUEST_TYPE): Subject<ITCIApplicationMonitoringResponse> {
        if (!this.monitorSubjectCollection[type]) {
            this.monitorSubjectCollection[type] = new Subject();
        }
        return this.monitorSubjectCollection[type];
    }

    setSelectedInstance(instance: ITCIMonitoringInstance): void {
        this.selectedInstance = instance;
        const type = TCI_APP_MONITORING_REQUEST_TYPE.APP_INSTANCE_SELECTED;
        const res = this.getGenericResponse(this.application.id, type);
        this.getAppMonitoringSubject(type).next(res);
    }

    getSelectedInstance(): Observable<ITCIMonitoringInstance> {
        return Observable.of(this.selectedInstance);
    }

    /**
     * Perform refresh for data based on TCI_APP_MONITORING_REQUEST_TYPE
     * @param input
     */
    refresh(input: ITCIApplicationsMonitoringRequest): Observable<ITCIApplicationMonitoringResponse> {
        return Observable.of(undefined);
    }
}
